import { Alert } from '@mui/material';
import Grid from '@mui/material/Grid';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import {
  BenefitActivationDTO,
  BenefitTemplateItemDTO,
  CreateBenefitActivationDTO,
  CustomBenefitDTO,
  EmployeeDTO,
  TenantDTO,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import { useTenant } from 'probonio-shared-ui/module/me';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BasicDialog } from '../../../component/dialog';
import { DialogLoadingButton } from '../../../component/dialog/DialogLoadingButton';
import { DatePickerControl } from '../../../component/form';
import { useBenefitContext } from '../BenefitContext';
import { ActivationDialogState } from './useActivationDialogState';

export interface BenefitActivationValues<T> {
  editMode?: boolean;
  startDate: Date;
  endDate?: Date;
  benefitOptions: T;
}

export interface ActivationContext {
  tenant?: TenantDTO;
  employee?: EmployeeDTO;
  queryClient: QueryClient;
  customBenefit?: CustomBenefitDTO;
}

export interface BaseActivationDialogProps<T> {
  activationDialogState: ActivationDialogState;
  isLoading?: boolean;
  activeEndDate?: DateTime;
  onCreateActivation: (
    baseValues: Pick<CreateBenefitActivationDTO, 'startDate' | 'endDate'>,
    benefitOptions: T,
    saveActivation: (create: CreateBenefitActivationDTO) => void | Promise<void>,
    context?: ActivationContext,
  ) => void | Promise<void>;
  onUpdateActivation: (
    baseValues: Pick<UpdateBenefitActivationDTO, 'startDate' | 'endDate'>,
    benefitOptions: T,
    saveActivation: (update: UpdateBenefitActivationDTO) => void | Promise<void>,
    context?: ActivationContext,
  ) => void | Promise<void>;
  onResetBenefitOptions: (selectedActivation?: BenefitActivationDTO | BenefitTemplateItemDTO, context?: ActivationContext) => T;
  onSaveCreateBenefitActivationDTO: (create: CreateBenefitActivationDTO) => void | Promise<void>;
  onSaveUpdateBenefitActivationDTO?: (benefitActivationId: string, update: UpdateBenefitActivationDTO) => void | Promise<void>;

  warnings?: React.ReactNode;
  header?: React.ReactNode;
  fields?: React.ReactNode;
  inlineField?: React.ReactNode;
  createLabel?: string;
  earliestStartDate?: () => DateTime;
  /** the employee's termination date */
  terminationDate?: DateTime;
  employee?: EmployeeDTO;
  customTitleKey?: string;
  progress?: number;
}

export const BaseActivationDialog = <T extends object>({
  activationDialogState,
  isLoading,
  activeEndDate,
  onCreateActivation,
  onUpdateActivation,
  onSaveCreateBenefitActivationDTO,
  onSaveUpdateBenefitActivationDTO,
  onResetBenefitOptions,
  warnings,
  header,
  fields,
  inlineField,
  createLabel,
  earliestStartDate,
  terminationDate,
  employee,
  customTitleKey,
  progress,
}: BaseActivationDialogProps<T>): React.JSX.Element => {
  const { t } = useTranslation('benefitsModule');
  const { tenant } = useTenant();
  const { customBenefit } = useBenefitContext();
  const queryClient = useQueryClient();
  const { activation: selectedActivation, dialogState } = activationDialogState;
  const formMethods = useForm<BenefitActivationValues<T>>();
  const { control, handleSubmit, setValue, reset, formState } = formMethods;
  const startDate = useWatch({ control, name: 'startDate' });
  const endDate = useWatch({ control, name: 'endDate' });
  const startOfMonth = DateTime.now().startOf('month').toJSDate();

  useEffect(() => {
    if (!endDate) return;
    if (DateTime.fromJSDate(startDate).endOf('month') > DateTime.fromJSDate(endDate).endOf('month')) {
      setValue('endDate', startDate);
    }
  }, [startDate, endDate, setValue]);

  const minStartDate = useMemo(() => {
    return (activeEndDate ? activeEndDate.plus({ months: 1 }) : earliestStartDate?.() || DateTime.now().startOf('month')).toJSDate();
  }, [activeEndDate, earliestStartDate]);

  const maxDate = useMemo(
    () => (terminationDate ? terminationDate.endOf('month') : DateTime.now().plus({ years: 10 })).toJSDate(),
    [terminationDate],
  );

  // No valid activation period exists when the earliest selectable start is already after the termination cap
  const hasNoValidRange = !selectedActivation && DateTime.fromJSDate(minStartDate) > DateTime.fromJSDate(maxDate);

  const activationContext = useMemo(
    (): ActivationContext => ({ tenant, employee, queryClient, customBenefit }),
    [tenant, employee, queryClient, customBenefit],
  );

  useEffect(() => {
    if (!dialogState.isOpen || !tenant) return;
    reset({
      editMode: !!selectedActivation,
      startDate: selectedActivation ? new Date(selectedActivation.startDate) : minStartDate,
      endDate: selectedActivation?.endDate ? new Date(selectedActivation.endDate) : undefined,
      benefitOptions: onResetBenefitOptions && onResetBenefitOptions(selectedActivation, activationContext),
    });
  }, [dialogState.isOpen, selectedActivation, reset, onResetBenefitOptions, minStartDate, activationContext, tenant]);

  const handleSaveActivation = useCallback(
    async ({ startDate, endDate, benefitOptions }: BenefitActivationValues<T>) => {
      try {
        if (!selectedActivation) {
          await onCreateActivation(
            {
              startDate: DateTime.fromJSDate(startDate).toFormat('yyyy-MM'),
              endDate: endDate ? DateTime.fromJSDate(endDate).toFormat('yyyy-MM') : undefined,
            },
            benefitOptions,
            onSaveCreateBenefitActivationDTO,
            activationContext,
          );
        } else if (onUpdateActivation && onSaveUpdateBenefitActivationDTO) {
          await onUpdateActivation(
            {
              startDate:
                startDate &&
                DateTime.fromJSDate(startDate).toFormat('yyyy-MM') !== DateTime.fromISO(selectedActivation.startDate).toFormat('yyyy-MM')
                  ? DateTime.fromJSDate(startDate).toFormat('yyyy-MM')
                  : undefined,
              endDate: endDate ? DateTime.fromJSDate(endDate).toFormat('yyyy-MM') : null,
            },
            benefitOptions,
            update => onSaveUpdateBenefitActivationDTO(selectedActivation.id, update),
            activationContext,
          );
        }
      } catch (err) {
        if (!(err instanceof AxiosError)) {
          // rethrow non-axios errors as they are probably not handled
          throw err;
        }
        // do not close dialog on error; error handling is done in mutations already
        return;
      }
      dialogState.handleClose();
    },
    [
      dialogState,
      onCreateActivation,
      onSaveCreateBenefitActivationDTO,
      onSaveUpdateBenefitActivationDTO,
      onUpdateActivation,
      selectedActivation,
      activationContext,
    ],
  );

  let title = t('dialogCreateTitle');
  if (selectedActivation) {
    title = t('dialogEditTitle');
  } else if (customTitleKey) {
    title = t(customTitleKey);
  }

  return (
    <BasicDialog
      dialogState={dialogState}
      title={title}
      content={
        <form data-test-id="date-picker-control" onSubmit={handleSubmit(handleSaveActivation)}>
          <FormProvider {...formMethods}>
            {header}
            {warnings}
            {hasNoValidRange ? (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {t('noValidActivationPeriod')}
              </Alert>
            ) : null}
            {selectedActivation && DateTime.fromISO(selectedActivation.startDate).diffNow('months').months <= -1 ? (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {t([`benefitSplitAlert.${selectedActivation.benefit}`, 'benefitSplitAlert.default'])}
              </Alert>
            ) : null}
            <Grid
              container
              columns={inlineField ? 16 : 12}
              columnSpacing={inlineField ? 1 : 2}
              sx={{
                marginBottom: 1,
              }}
            >
              <Grid size={6}>
                <DatePickerControl
                  dataTestId="date-picker-start"
                  minDate={minStartDate}
                  maxDate={maxDate}
                  control={control}
                  name="startDate"
                  label={t('fields.dateFrom')}
                  helperText={
                    selectedActivation
                      ? ''
                      : earliestStartDate && earliestStartDate() >= DateTime.now().plus({ months: 1 }).startOf('month')
                        ? t('helperText.dateNextMonth')
                        : t('helperText.dateFrom')
                  }
                  fullWidth
                  margin="dense"
                  rules={{
                    required: !selectedActivation,
                    validate: {
                      minDate: (value: unknown) =>
                        (value instanceof Date && DateTime.fromJSDate(value) >= DateTime.now().startOf('month')) || !!selectedActivation,
                    },
                  }}
                  disabled={selectedActivation && DateTime.fromISO(selectedActivation.startDate) < DateTime.now()}
                  monthYearPicker
                />
              </Grid>
              <Grid size={6}>
                <DatePickerControl
                  dataTestId="date-picker-end"
                  dateFormat="MMMM y"
                  control={control}
                  name="endDate"
                  label={t('fields.dateTill')}
                  helperText={t('helperText.dateTill')}
                  fullWidth
                  margin="dense"
                  minDate={startDate > startOfMonth ? startDate : startOfMonth}
                  maxDate={maxDate}
                  monthYearPicker
                />
              </Grid>
              {inlineField && <Grid size={4}>{inlineField}</Grid>}
            </Grid>
            {fields}
          </FormProvider>
        </form>
      }
      actions={
        <DialogLoadingButton
          loading={isLoading || formState.isSubmitting}
          loadingVariant="determinate"
          loadingValue={progress}
          onClick={handleSubmit(handleSaveActivation)}
          disabled={hasNoValidRange || formMethods.formState.errors?.root?.message === 'error'}
          data-test-id="activate-dialog-button-submit"
        >
          {selectedActivation ? t('common:buttons.save') : t(createLabel || 'common:buttons.activate')}
        </DialogLoadingButton>
      }
    />
  );
};
